/**
 * MediaUploader - Reusable component for uploading thumbnails, videos, etc.
 * Supports both file upload and URL input with a premium UI
 */

import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Link2,
  Image,
  Video,
  X,
  Check,
  Loader2,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Types
export type MediaType = 'thumbnail' | 'video' | 'preview-video';

interface MediaUploaderProps {
  type: MediaType;
  value?: string; // Current URL value
  onChange: (url: string) => void;
  onUpload?: (file: File, onProgress?: (progress: number) => void) => Promise<{ url: string }>;
  courseId?: string; // For upload context
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  maxSize?: number; // in MB
  accept?: string; // file input accept attribute
}

// Config by type
const TYPE_CONFIG: Record<MediaType, {
  icon: React.ElementType;
  label: string;
  acceptTypes: string;
  maxSizeMB: number;
  previewAspect: string;
  tips: string[];
}> = {
  thumbnail: {
    icon: Image,
    label: 'Course Thumbnail',
    acceptTypes: 'image/jpeg,image/png,image/webp,image/avif,image/gif',
    maxSizeMB: 5,
    previewAspect: 'aspect-video',
    tips: [
      'Recommended: 1280×720 pixels (16:9)',
      'Formats: JPEG, PNG, WebP, AVIF',
      'Max size: 5MB',
    ],
  },
  video: {
    icon: Video,
    label: 'Lesson Video',
    acceptTypes: 'video/mp4,video/webm,video/quicktime',
    maxSizeMB: 2048,
    previewAspect: 'aspect-video',
    tips: [
      'Recommended: 1080p or higher',
      'Formats: MP4, WebM, MOV',
      'Max size: 2GB',
    ],
  },
  'preview-video': {
    icon: Video,
    label: 'Preview Video',
    acceptTypes: 'video/mp4,video/webm,video/quicktime',
    maxSizeMB: 500,
    previewAspect: 'aspect-video',
    tips: [
      'Keep it under 2 minutes',
      'Showcase your teaching style',
      'Max size: 500MB',
    ],
  },
};

export default function MediaUploader({
  type,
  value,
  onChange,
  onUpload,
  className,
  placeholder,
  disabled,
  maxSize,
  accept,
}: MediaUploaderProps) {
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;
  
  const [mode, setMode] = useState<'upload' | 'url'>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState(value || '');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const effectiveMaxSize = maxSize || config.maxSizeMB;
  const effectiveAccept = accept || config.acceptTypes;

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);

    // Validate file type
    const allowedTypes = effectiveAccept.split(',');
    if (!allowedTypes.some(t => file.type.startsWith(t.split('/')[0]) || file.type === t)) {
      setError(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    // Validate file size
    const maxBytes = effectiveMaxSize * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File too large. Maximum size: ${effectiveMaxSize}MB`);
      return;
    }

    if (!onUpload) {
      // If no upload handler, create a local preview URL
      const url = URL.createObjectURL(file);
      onChange(url);
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Use the onProgress callback for real progress tracking
      const result = await onUpload(file, (progress) => {
        setUploadProgress(progress);
      });
      
      setUploadProgress(100);
      onChange(result.url);
      
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [onUpload, onChange, effectiveAccept, effectiveMaxSize]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isUploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [disabled, isUploading, handleFileSelect]);

  // URL submission
  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    try {
      new URL(urlInput);
      setError(null);
      onChange(urlInput);
    } catch {
      setError('Invalid URL format');
    }
  };

  // Clear value
  const handleClear = () => {
    onChange('');
    setUrlInput('');
    setError(null);
  };

  const isImage = type === 'thumbnail';

  return (
    <div className={cn('space-y-3', className)}>
      {/* Mode Toggle */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMode('upload')}
          disabled={disabled || isUploading}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            mode === 'upload'
              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
              : 'bg-white/5 text-white/50 hover:text-white/70 border border-transparent'
          )}
        >
          <Upload className="w-4 h-4" />
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          disabled={disabled || isUploading}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            mode === 'url'
              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
              : 'bg-white/5 text-white/50 hover:text-white/70 border border-transparent'
          )}
        >
          <Link2 className="w-4 h-4" />
          Paste URL
        </button>
      </div>

      {/* Upload Zone or URL Input */}
      <AnimatePresence mode="wait">
        {mode === 'upload' ? (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
              className={cn(
                'relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                isDragging
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-white/10 hover:border-white/20 bg-white/[0.02]',
                disabled && 'opacity-50 cursor-not-allowed',
                isUploading && 'pointer-events-none'
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={effectiveAccept}
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
                disabled={disabled || isUploading}
              />

              {isUploading ? (
                <div className="space-y-4">
                  <Loader2 className="w-10 h-10 text-purple-400 mx-auto animate-spin" />
                  <div className="w-full max-w-xs mx-auto">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <p className="text-sm text-white/50 mt-2">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className={cn(
                    'w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center',
                    'bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20'
                  )}>
                    <Icon className="w-8 h-8 text-purple-400" />
                  </div>
                  <p className="text-white font-medium mb-1">
                    {isDragging ? 'Drop your file here' : 'Drag & drop or click to upload'}
                  </p>
                  <p className="text-white/40 text-sm">
                    {placeholder || `${config.label} (max ${effectiveMaxSize}MB)`}
                  </p>
                </>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="url"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                  placeholder={`https://example.com/${isImage ? 'thumbnail.jpg' : 'video.mp4'}`}
                  className="input pl-12 pr-4"
                  disabled={disabled}
                />
              </div>
              <button
                type="button"
                onClick={handleUrlSubmit}
                disabled={disabled || !urlInput.trim()}
                className="btn-primary px-6 disabled:opacity-50"
              >
                <Check className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 rounded-lg px-4 py-2"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview */}
      <AnimatePresence>
        {value && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative rounded-xl overflow-hidden border border-white/10 bg-black/50"
          >
            {isImage ? (
              <img
                src={value}
                alt="Preview"
                className={cn('w-full object-cover max-h-48', config.previewAspect)}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  setError('Failed to load image preview');
                }}
              />
            ) : (
              <video
                src={value}
                controls
                className={cn('w-full', config.previewAspect)}
                onError={() => setError('Failed to load video preview')}
              />
            )}

            {/* Actions */}
            <div className="absolute top-2 right-2 flex gap-2">
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={handleClear}
                className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-red-400 transition-colors"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Success indicator */}
            <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-emerald-500/20 text-emerald-400 text-sm px-3 py-1 rounded-full">
              <Check className="w-4 h-4" />
              <span className="truncate max-w-[200px]">
                {value.includes('blob:') ? 'Local preview' : 'Uploaded'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tips */}
      {!value && (
        <div className="flex flex-wrap gap-2">
          {config.tips.map((tip, i) => (
            <span
              key={i}
              className="text-xs text-white/30 bg-white/5 px-2 py-1 rounded"
            >
              {tip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
